// Which price a visit starts from — the pure half (pinned by
// scripts/pricing.test.mjs). Customers have a regular price for staying on
// their cycle and a dearer first-clean / ad-hoc price. The ad-hoc price is the
// one when there is no cycle, or when the visit falls well past the cycle
// (missed = more than half a cycle late, Liam 2026-09-11).

export const MISSED_AFTER_CYCLES = 1.5

export type PriceWhy = 'regular' | 'adhoc' | 'missed' | 'none'

export interface PriceCustomer {
  cycle_days: number | null
  reg_price_pence: number | null
  first_clean_price_pence: number | null
}

export interface PriceChoice {
  pence: number | null
  why: PriceWhy
  lateDays: number | null   // how far past the due date the visit is (missed only)
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86_400_000)
}

export function priceFor(c: PriceCustomer, visitDate: string | null | undefined, lastClean: string | null | undefined): PriceChoice {
  const regular = c.reg_price_pence ?? null
  const adhoc = c.first_clean_price_pence ?? null
  const cycle = c.cycle_days && c.cycle_days > 0 ? c.cycle_days : null
  if (!cycle) {
    // No cycle agreed: ad-hoc price when there is one, else whatever is on file.
    if (adhoc !== null) return { pence: adhoc, why: 'adhoc', lateDays: null }
    return { pence: regular, why: regular === null ? 'none' : 'regular', lateDays: null }
  }
  if (lastClean && visitDate) {
    const gap = daysBetween(lastClean, visitDate)
    if (gap > cycle * MISSED_AFTER_CYCLES) {
      const late = gap - cycle
      if (adhoc !== null) return { pence: adhoc, why: 'missed', lateDays: late }
      return { pence: regular, why: regular === null ? 'none' : 'regular', lateDays: late }
    }
  }
  return { pence: regular, why: regular === null ? 'none' : 'regular', lateDays: null }
}

// The grid's price cell stays one figure. Only a customer with NO cycle (they
// pay ad-hoc every time) shows the ad-hoc price beside it — everyone else
// sees the regular price alone, and the booking panel handles missed cycles.
export function priceCellText(c: PriceCustomer, money: (p: number | null) => string): string {
  const reg = c.reg_price_pence ?? null
  const adhoc = c.first_clean_price_pence ?? null
  const onCycle = !!(c.cycle_days && c.cycle_days > 0)
  if (onCycle || adhoc === null || adhoc === reg) return money(reg)
  return `${money(reg)} · ad-hoc ${money(adhoc)}`
}
